import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import {
  adminApi,
  type NormalizedProductFields,
  type ReviewQueueDetail,
} from '../../services/adminApi';

const CALIBER_OPTIONS = [
  '9mm Luger',
  '.223 Remington',
  '5.56 NATO',
  '.308 Winchester',
  '.300 Blackout',
  '.22 LR',
  '12 Gauge',
];
const BULLET_TYPE_OPTIONS = ['FMJ', 'JHP', 'HP', 'SP', 'Match', 'Frangible', 'Lead'];
const CASE_MATERIAL_OPTIONS = ['Brass', 'Steel', 'Aluminum', 'Nickel'];
const USE_CASE_OPTIONS = ['range', 'defense', 'hunting', 'competition'];

interface FormState extends NormalizedProductFields {
  notes: string;
  aliases: string[];
  aliasDraft: string;
}

function emptyForm(): FormState {
  return {
    brand: '',
    productLine: '',
    caliber: '',
    bulletWeightGrains: null,
    bulletType: '',
    caseMaterial: '',
    useCases: [],
    notes: '',
    aliases: [],
    aliasDraft: '',
  };
}

function fromDetail(detail: ReviewQueueDetail): FormState {
  const np = detail.normalizedProduct;
  return {
    brand: np?.brand ?? '',
    productLine: np?.productLine ?? '',
    caliber: np?.caliber ?? '',
    bulletWeightGrains: np?.bulletWeightGrains ?? null,
    bulletType: np?.bulletType ?? '',
    caseMaterial: np?.caseMaterial ?? '',
    useCases: np?.useCases ?? [],
    notes: detail.item.notes ?? '',
    aliases: [],
    aliasDraft: '',
  };
}

/**
 * Admin review queue item detail / edit page.
 *
 * Reviewer can:
 *   - Inspect the raw listing (title, source URL, JSON of extracted fields).
 *   - Edit the linked NormalizedProduct fields.
 *   - Add aliases (chip input).
 *   - Add notes.
 *   - Save & resolve, or Dismiss.
 */
export function ReviewQueueItemPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['admin', 'review-queue', id],
    queryFn: () => adminApi.getReviewQueueItem(id),
    enabled: id.length > 0,
  });

  const [form, setForm] = useState<FormState>(emptyForm);

  // Seed the form from the loaded detail, re-syncing if the query data changes.
  // "Adjust state during render" instead of a setState-in-effect.
  const [syncedDetail, setSyncedDetail] = useState(detailQuery.data);
  if (detailQuery.data && detailQuery.data !== syncedDetail) {
    setSyncedDetail(detailQuery.data);
    setForm(fromDetail(detailQuery.data));
  }

  const mutation = useMutation({
    mutationFn: async (status: 'resolved' | 'dismissed') => {
      const fields: NormalizedProductFields = {
        brand: form.brand || null,
        productLine: form.productLine || null,
        caliber: form.caliber || null,
        bulletWeightGrains: form.bulletWeightGrains ?? null,
        bulletType: form.bulletType || null,
        caseMaterial: form.caseMaterial || null,
        useCases: form.useCases ?? [],
      };
      return adminApi.patchReviewQueueItem(id, {
        status,
        normalizedProductFields: fields,
        notes: form.notes,
        addAliases: form.aliases,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'review-queue'] });
      navigate('/admin/review-queue');
    },
  });

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function addAlias() {
    const v = form.aliasDraft.trim();
    if (!v) return;
    setForm((f) => ({ ...f, aliases: [...f.aliases, v], aliasDraft: '' }));
  }

  function removeAlias(idx: number) {
    setForm((f) => ({ ...f, aliases: f.aliases.filter((_, i) => i !== idx) }));
  }

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading review queue item" />
      </Box>
    );
  }

  if (detailQuery.isError) {
    return (
      <Alert severity="error" role="alert">
        Failed to load item: {(detailQuery.error as Error).message}
      </Alert>
    );
  }

  if (!detailQuery.data) return null;

  const { item, rawListing, retailer } = detailQuery.data;

  return (
    <Box component="form" onSubmit={(e) => e.preventDefault()} aria-label="Review queue item">
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Review item {item.id.slice(0, 8)}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h2" sx={{ fontSize: '1.1rem', mb: 1 }}>
          Raw listing
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>Title:</strong> {rawListing?.rawTitle ?? '—'}
          </Typography>
          <Typography variant="body2">
            <strong>Source URL:</strong>{' '}
            {rawListing?.sourceUrl ? (
              <a href={rawListing.sourceUrl} target="_blank" rel="noreferrer noopener">
                {rawListing.sourceUrl}
              </a>
            ) : (
              '—'
            )}
          </Typography>
          <Typography variant="body2">
            <strong>Retailer:</strong> {retailer?.name ?? '—'}
          </Typography>
          <Typography variant="body2">
            <strong>Reason:</strong> {item.reason}
          </Typography>
          <Typography variant="body2">
            <strong>Confidence:</strong>{' '}
            {item.confidenceScore == null ? '—' : Number(item.confidenceScore).toFixed(2)}
          </Typography>
          {rawListing?.rawData !== undefined && (
            <Box
              component="pre"
              sx={{
                bgcolor: 'action.hover',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                fontSize: '0.8rem',
                overflow: 'auto',
                maxHeight: 240,
              }}
              aria-label="Extracted raw data"
            >
              {JSON.stringify(rawListing.rawData, null, 2)}
            </Box>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h2" sx={{ fontSize: '1.1rem', mb: 2 }}>
          Normalized fields
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Brand"
            value={form.brand ?? ''}
            onChange={(e) => update('brand', e.target.value)}
            fullWidth
          />
          <TextField
            label="Product line"
            value={form.productLine ?? ''}
            onChange={(e) => update('productLine', e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="caliber-label">Caliber</InputLabel>
            <Select
              labelId="caliber-label"
              id="caliber"
              value={form.caliber ?? ''}
              label="Caliber"
              onChange={(e) => update('caliber', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {CALIBER_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Bullet weight (grains)"
            type="number"
            value={form.bulletWeightGrains ?? ''}
            onChange={(e) =>
              update('bulletWeightGrains', e.target.value === '' ? null : Number(e.target.value))
            }
            inputProps={{ min: 0, step: 0.1 }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="bullet-type-label">Bullet type</InputLabel>
            <Select
              labelId="bullet-type-label"
              id="bullet-type"
              value={form.bulletType ?? ''}
              label="Bullet type"
              onChange={(e) => update('bulletType', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {BULLET_TYPE_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="case-material-label">Case material</InputLabel>
            <Select
              labelId="case-material-label"
              id="case-material"
              value={form.caseMaterial ?? ''}
              label="Case material"
              onChange={(e) => update('caseMaterial', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {CASE_MATERIAL_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="use-cases-label">Use cases</InputLabel>
            <Select
              labelId="use-cases-label"
              id="use-cases"
              multiple
              value={form.useCases ?? []}
              label="Use cases"
              onChange={(e) =>
                update(
                  'useCases',
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : (e.target.value as string[])
                )
              }
              renderValue={(selected) => (selected as string[]).join(', ')}
            >
              {USE_CASE_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h2" sx={{ fontSize: '1.1rem', mb: 1 }}>
          Aliases
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
          {form.aliases.map((a, idx) => (
            <Chip
              key={`${a}-${idx}`}
              label={a}
              onDelete={() => removeAlias(idx)}
              aria-label={`Remove alias ${a}`}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Add alias"
            size="small"
            value={form.aliasDraft}
            onChange={(e) => update('aliasDraft', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addAlias();
              }
            }}
            fullWidth
          />
          <Button variant="outlined" onClick={addAlias}>
            Add
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h2" sx={{ fontSize: '1.1rem', mb: 1 }}>
          Notes
        </Typography>
        <TextField
          label="Reviewer notes"
          multiline
          minRows={3}
          fullWidth
          value={form.notes}
          onChange={(e) => update('notes', e.target.value)}
          aria-describedby="notes-help"
        />
        <Typography id="notes-help" variant="caption" color="text.secondary">
          Free-text notes recorded with the item for audit.
        </Typography>
      </Paper>

      {mutation.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Save failed: {(mutation.error as Error).message}
        </Alert>
      )}

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => mutation.mutate('resolved')}
          disabled={mutation.isPending}
        >
          Save & resolve
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => mutation.mutate('dismissed')}
          disabled={mutation.isPending}
        >
          Dismiss
        </Button>
        <Button variant="text" onClick={() => navigate('/admin/review-queue')}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
}

export default ReviewQueueItemPage;
